import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FiSave, FiArrowLeft, FiX, FiSearch, FiCheck, FiCalendar } from 'react-icons/fi';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import toast from 'react-hot-toast';
import { api } from '../../../api/api';

const EditOffer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [products, setProducts] = useState([]); // Add products state

  const [formData, setFormData] = useState({
    name: '',
    offerType: 'PERCENTAGE', // Make sure default is uppercase
    value: '',
    selectedItems: [],
    startDate: null, // Changed to null
    endDate: null, // Changed to null
  });

  // Add these new states
  const [isProductSelectorOpen, setIsProductSelectorOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Add useEffect to fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsData = await api.getProducts();
        setProducts(productsData);
      } catch (error) {
        console.error('Error fetching products:', error);
        toast.error('Failed to load products');
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    const loadOffer = async () => {
      try {
        setIsLoading(true);
        const response = await api.getOffer(id);
        
        if (!response) {
          throw new Error('Offer not found');
        }

        // Format the data to match your form structure (without description)
        const formattedOffer = {
          name: response.name,
          offerType: response.offer_type.toLowerCase(),
          value: response.discount_value,
          selectedItems: response.selected_items || [],
          startDate: new Date(response.start_date), // Convert to Date object
          endDate: new Date(response.end_date), // Convert to Date object
        };

        setFormData(formattedOffer);
        
        // Load selected products details
        if (response.selected_items?.length > 0) {
          const selectedProductsDetails = await api.getProductsByIds(response.selected_items);
          setProducts(prevProducts => {
            const existingProductIds = new Set(prevProducts.map(p => p.id));
            const newProducts = selectedProductsDetails.filter(p => !existingProductIds.has(p.id));
            return [...prevProducts, ...newProducts];
          });
        }
      } catch (error) {
        console.error('Error loading offer:', error);
        toast.error('Failed to load offer');
        navigate('/admin/offers');
      } finally {
        setIsLoading(false);
      }
    };

    loadOffer();
  }, [id, navigate]);

  // Add these helper functions
  const handleProductToggle = (productId) => {
    setFormData(prev => ({
      ...prev,
      selectedItems: prev.selectedItems.includes(productId)
        ? prev.selectedItems.filter(id => id !== productId)
        : [...prev.selectedItems, productId]
    }));
  };

  const getSelectedProducts = () => {
    return products.filter(product => formData.selectedItems.includes(product.id));
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validation
      if (!formData.name || formData.value === '' || !formData.startDate || !formData.endDate) {
        throw new Error('Please fill in all required fields');
      }

      if (formData.offerType === 'PERCENTAGE' && 
          (parseFloat(formData.value) < 0 || parseFloat(formData.value) > 100)) {
        throw new Error('Percentage must be between 0 and 100');
      }

      // Get current date without time component
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      // Format start and end dates
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);

      // Validate dates
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        throw new Error('Invalid date format');
      }

      if (startDate < now) {
        throw new Error('Start date cannot be in the past');
      }

      if (endDate <= startDate) {
        throw new Error('End date must be after start date');
      }

      const offerData = {
        name: formData.name.trim(),
        offerType: formData.offerType,
        value: parseFloat(formData.value),
        selectedItems: Array.isArray(formData.selectedItems) ? formData.selectedItems : [],
        startDate: startDate.toISOString().split('.')[0] + 'Z', // Format: "2024-02-20T15:30:00Z"
        endDate: endDate.toISOString().split('.')[0] + 'Z'     // Format: "2024-02-20T15:30:00Z"
      };

      console.log('Submitting offer data:', offerData);

      await api.updateOffer(id, offerData);
      
      toast.success('Offer updated successfully!');
      navigate('/admin/offers');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.message || 'Failed to update offer');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6 p-4 md:p-6"
    >
      <Helmet>
        <title>Edit Offer | Admin Dashboard</title>
      </Helmet>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-2">
          <button 
            onClick={() => navigate('/admin/offers')}
            className="p-1 rounded-full hover:bg-gray-100"
          >
            <FiArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-semibold text-gray-900">Edit Offer</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Offer Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="e.g., Summer Sale 2024"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Offer Type *
              </label>
              <select
                value={formData.offerType}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  offerType: e.target.value.toUpperCase(),
                  // Reset value when changing offer type
                  value: ''
                }))}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
              >
                <option value="PERCENTAGE">Percentage Discount</option>
                <option value="FIXED">Fixed Amount</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {formData.offerType === 'PERCENTAGE' ? 'Discount Percentage *' : 'Discount Amount *'}
              </label>
              <input
                type="number"
                value={formData.value}
                onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder={formData.offerType === 'PERCENTAGE' ? 'Enter percentage' : 'Enter amount'}
                min="0"
                max={formData.offerType === 'PERCENTAGE' ? "100" : undefined}
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Products *
              </label>
              <div className="relative">
                <div
                  className="min-h-[42px] px-3 py-2 border border-gray-300 rounded-xl shadow-sm cursor-pointer bg-white"
                  onClick={() => setIsProductSelectorOpen(!isProductSelectorOpen)}
                >
                  <div className="flex flex-wrap gap-2">
                    {getSelectedProducts().length > 0 ? (
                      getSelectedProducts().map(product => (
                        <span
                          key={product.id}
                          className="inline-flex items-center px-2 py-1 rounded-md text-sm bg-indigo-100 text-indigo-800"
                        >
                          {product.name}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleProductToggle(product.id);
                            }}
                            className="ml-1 hover:text-indigo-500"
                          >
                            <FiX className="h-4 w-4" />
                          </button>
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-500">Select products...</span>
                    )}
                  </div>
                </div>

                {isProductSelectorOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white rounded-xl shadow-lg border border-gray-200">
                    <div className="p-2 border-b border-gray-200">
                      <div className="relative">
                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                        <input
                          type="text"
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Search products..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>

                    <div className="max-h-60 overflow-auto">
                      {filteredProducts.length > 0 ? (
                        filteredProducts.map(product => (
                          <div
                            key={product.id}
                            className={`
                              px-4 py-2 cursor-pointer hover:bg-gray-50 flex items-center justify-between
                              ${formData.selectedItems.includes(product.id) ? 'bg-indigo-50' : ''}
                            `}
                            onClick={() => handleProductToggle(product.id)}
                          >
                            <span className="text-sm text-gray-900">{product.name}</span>
                            {formData.selectedItems.includes(product.id) && (
                              <FiCheck className="h-4 w-4 text-indigo-600" />
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-sm text-gray-500">
                          No products found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="sm:col-span-2 md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date *
              </label>
              <div className="relative datepicker-icon-container">
                <FiCalendar className="datepicker-icon" />
                <DatePicker
                  selected={formData.startDate}
                  onChange={(date) => setFormData({ ...formData, startDate: date })}
                  selectsStart
                  startDate={formData.startDate}
                  endDate={formData.endDate}
                  minDate={new Date()} // Prevent past dates
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="yyyy-MM-dd HH:mm"
                  placeholderText="Select start date and time"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  wrapperClassName="w-full"
                  calendarClassName="shadow-lg"
                  showPopperArrow={false}
                  autoComplete="off"
                  required
                />
              </div>
            </div>

            <div className="sm:col-span-2 md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date *
              </label>
              <div className="relative datepicker-icon-container">
                <FiCalendar className="datepicker-icon" />
                <DatePicker
                  selected={formData.endDate}
                  onChange={(date) => setFormData({ ...formData, endDate: date })}
                  selectsEnd
                  startDate={formData.startDate}
                  endDate={formData.endDate}
                  minDate={formData.startDate || new Date()}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="yyyy-MM-dd HH:mm"
                  placeholderText="Select end date and time"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  wrapperClassName="w-full"
                  calendarClassName="shadow-lg"
                  showPopperArrow={false}
                  autoComplete="off"
                  required
                />
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/admin/offers')}
            className="px-4 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default EditOffer;





















